import { ActionIcon, Badge, Group, Select, Stack, Table, TextInput, Title } from '@mantine/core';
import { useState } from 'react';

import { PracticeRoster, usePracticesList } from '../../features/practices';
import { formatDateTimeIn } from '../../shared/format';
import { CursorPager, PageFallback, hasItems, useCursorPager } from '../../shared/ui';

const STATUSES = [
  { value: 'scheduled', label: 'Запланирована' },
  { value: 'cancelled', label: 'Отменена' },
];

const STATUS_LABEL = Object.fromEntries(STATUSES.map((status) => [status.value, status.label]));

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * День из `<input type="date">` — в мгновение для API. Граница окна — полночь по часам
 * администратора: `from` включительно, `to` — полночь *следующего* дня, чтобы выбранный
 * «до» день попал в окно (API читает `to` как исключающую границу).
 */
function dayStart(day: string, plusDays = 0): string | undefined {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return undefined;
  const date = new Date(`${day}T00:00`);
  if (Number.isNaN(date.getTime())) return undefined;
  date.setDate(date.getDate() + plusDays);
  return date.toISOString();
}

/**
 * Практики всех учителей (CAP-5) с росписью, раскрываемой по практике (CAP-6). Время —
 * в зоне практики. «Занято» — `confirmedCount` из ответа списка: считает API, не панель.
 */
export function PracticesPage() {
  const [teacher, setTeacher] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [opened, setOpened] = useState<string | null>(null);
  // Идентификатор учителя уходит в запрос только целым: половина UUID — это `400` от API.
  const teacherFilter = UUID.test(teacher.trim()) ? teacher.trim() : undefined;
  const filters = {
    teacher: teacherFilter,
    from: dayStart(from),
    to: dayStart(to, 1),
    status: status ?? undefined,
  };
  const pager = useCursorPager(JSON.stringify(filters));
  const page = usePracticesList(filters, pager.cursor);

  return (
    <Stack gap="md">
      <Title order={2}>Практики</Title>
      <Group align="flex-end">
        <TextInput
          label="Учитель (id аккаунта)"
          placeholder="uuid"
          value={teacher}
          onChange={(event) => setTeacher(event.currentTarget.value)}
          w={340}
        />
        <TextInput
          label="С"
          type="date"
          value={from}
          onChange={(event) => setFrom(event.currentTarget.value)}
          w={170}
        />
        <TextInput
          label="По"
          type="date"
          value={to}
          onChange={(event) => setTo(event.currentTarget.value)}
          w={170}
        />
        <Select
          label="Статус"
          data={STATUSES}
          value={status}
          onChange={setStatus}
          clearable
          w={180}
        />
      </Group>

      <PageFallback query={page} empty="Практик нет." />
      {hasItems(page) ? (
        <Table striped>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={40} />
              <Table.Th>Начало</Table.Th>
              <Table.Th>Практика</Table.Th>
              <Table.Th>Учитель</Table.Th>
              <Table.Th>Адрес</Table.Th>
              <Table.Th>Мест</Table.Th>
              <Table.Th>Занято</Table.Th>
              <Table.Th>Статус</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {page.data?.items.map((practice) => {
              const isOpen = opened === practice.id;
              return [
                <Table.Tr key={practice.id}>
                  <Table.Td>
                    <ActionIcon
                      variant="subtle"
                      size="sm"
                      aria-label={`Роспись: ${practice.title}`}
                      aria-expanded={isOpen}
                      onClick={() => setOpened(isOpen ? null : practice.id)}
                    >
                      {isOpen ? '▾' : '▸'}
                    </ActionIcon>
                  </Table.Td>
                  <Table.Td>
                    {formatDateTimeIn(practice.startsAt, practice.timeZone)}
                    <br />
                    <small>
                      {practice.timeZone}, {practice.durationMinutes} мин
                    </small>
                  </Table.Td>
                  <Table.Td>{practice.title}</Table.Td>
                  <Table.Td>{practice.teacher.displayName ?? 'Без имени'}</Table.Td>
                  <Table.Td>{practice.address}</Table.Td>
                  <Table.Td>{practice.capacity}</Table.Td>
                  <Table.Td>{practice.confirmedCount}</Table.Td>
                  <Table.Td>
                    <Badge
                      variant="light"
                      color={practice.status === 'cancelled' ? 'gray' : 'green'}
                    >
                      {STATUS_LABEL[practice.status] ?? practice.status}
                    </Badge>
                  </Table.Td>
                </Table.Tr>,
                // Роспись запрашивается только раскрытой: закрытая строка — ноль запросов.
                isOpen ? (
                  <Table.Tr key={`${practice.id}-roster`}>
                    <Table.Td colSpan={8} px="xl" py="sm">
                      <PracticeRoster practiceId={practice.id} practiceStatus={practice.status} />
                    </Table.Td>
                  </Table.Tr>
                ) : null,
              ];
            })}
          </Table.Tbody>
        </Table>
      ) : null}
      <CursorPager query={page} pager={pager} />
    </Stack>
  );
}
